/**
 * KAN-889: manual save of the decision-maker contact ("Контакты ЛПР").
 *
 * One request: PATCH /clients/<id>/ with { lead_contacts: {...} } carrying only
 * the fields changed against the current contact (a cleared field goes as "").
 * Nothing changed: no request, the form just closes. On success the client
 * card, call targets, attention lists, the client list and the lead card are
 * re-read. A 400 {"lead_contacts": {"phone": ["..."]}} is turned into a map
 * field -> text to show under the fields.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { clientLeadCardQueryKey, clientQueryKey } from '@/services/api/clientQueries';
import { ATTENTION_QUERY_KEY } from '@/services/api/queryKeys';
import { callTargetsQueryKey } from '@/hooks/useCallTargets';
import { fieldErrorMap } from '@/lib/fieldErrors';
import type { LeadContact, LeadContactsPayload } from '@/types/client';

export const LEAD_CONTACT_FIELDS = ['name', 'role', 'phone', 'telegram', 'email'] as const;
export type LeadContactField = (typeof LEAD_CONTACT_FIELDS)[number];

/** Form values from the current contact (empty form without one). */
export function leadContactsFormValues(contact?: LeadContact): LeadContactsPayload {
  return {
    name: contact?.name ?? '',
    role: contact?.role ?? '',
    phone: contact?.phone ?? '',
    telegram: contact?.telegram ?? '',
    email: contact?.email ?? '',
  };
}

type LeadContactsChanges = Partial<LeadContactsPayload>;

/** Value as it is compared: trimmed, Telegram without the leading @. */
function normalized(field: LeadContactField, value: string): string {
  const trimmed = value.trim();
  return field === 'telegram' ? trimmed.replace(/^@+/, '') : trimmed;
}

/**
 * Request body: only the fields changed against the current contact, trimmed.
 * null when nothing changed (a bot-written invalid value left untouched is
 * not re-sent, so it does not block saving another field).
 */
export function buildLeadContactsPayload(
  values: LeadContactsPayload,
  contact?: LeadContact
): { lead_contacts: LeadContactsChanges } | null {
  const initial = leadContactsFormValues(contact);
  const changes: LeadContactsChanges = {};
  for (const field of LEAD_CONTACT_FIELDS) {
    if (normalized(field, values[field]) !== normalized(field, initial[field])) {
      changes[field] = values[field].trim();
    }
  }
  return Object.keys(changes).length > 0 ? { lead_contacts: changes } : null;
}

/**
 * Field errors of a 400 body {"lead_contacts": {...}}; a flat
 * {"lead_contacts": ["..."]} goes under the key "form".
 */
export function leadContactsErrors(err: unknown): Partial<Record<LeadContactField | 'form', string>> {
  if (!err) return {};
  const data = (err as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  const nested = (data as Record<string, unknown>).lead_contacts;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return fieldErrorMap({ response: { data: nested } });
  }
  const flat = fieldErrorMap(err);
  return flat.lead_contacts ? { form: flat.lead_contacts } : {};
}

export function useLeadContactsSave(clientId: string, contact?: LeadContact, onSaved?: () => void) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: { lead_contacts: LeadContactsChanges }) => clientsApi.update(clientId, payload),
    onSuccess: () => {
      for (const queryKey of [
        clientQueryKey(clientId),
        callTargetsQueryKey(clientId),
        ATTENTION_QUERY_KEY,
        ['clients'],
        clientLeadCardQueryKey(clientId),
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
      onSaved?.();
      toast.success('Контакты ЛПР сохранены');
    },
    onError: (err) => {
      if (Object.keys(leadContactsErrors(err)).length === 0) {
        toast.error('Не удалось сохранить контакты ЛПР');
      }
    },
  });

  /** Saves the changed fields; nothing changed: closes without a request. */
  const submit = (values: LeadContactsPayload) => {
    const payload = buildLeadContactsPayload(values, contact);
    if (payload) mutation.mutate(payload);
    else onSaved?.();
  };

  return { ...mutation, submit };
}
